import { z } from 'zod';
import {
	AbsenteeismCategory,
	ExtraTripMotive,
} from '@/constants/ExtraTripConstants';

export const DateIntervalSchema = z.object({
	id: z.string(),
	dateRange: z
		.object({
			startDate: z.string().optional(),
			endDate: z.string().optional(),
		})
		.optional(),
	entryTime: z.string(),
	exitTime: z.string(),
	location: z.union([z.string(), z.number()]).nullable(),
	confinement: z.boolean(),
	needTransport: z.boolean(),
});

export const TransportDaySchema = z.object({
	id: z.string(),
	date: z.string(),
	entryTime: z.string(),
	exitTime: z.string(),
	transportType: z.union([z.string(), z.number()]).nullable(),
	location: z.union([z.string(), z.number()]).nullable(),
});

export const ExtraTripFormSchema = z
	.object({
		motive: z.nativeEnum(ExtraTripMotive),
		detail: z.string(),
		category: z.nativeEnum(AbsenteeismCategory).nullable(),
		coveredEmployee: z.union([z.string(), z.number()]).nullable(),
		coveredEmployeeManagement: z.string(),
		coveredEmployeeStructure: z.string(),
		coveredEmployeeTeam: z.string(),
		vacancy: z.string(),
		team: z.union([z.string(), z.number()]).nullable(),
		teamStructure: z.union([z.string(), z.number()]).nullable(),
		teamManagement: z.union([z.string(), z.number()]).nullable(),
		teamBoard: z.union([z.string(), z.number()]).nullable(),
		dateIntervals: z.array(DateIntervalSchema).min(1),
		transportDays: z.array(TransportDaySchema),
		repeatLocation: z.record(z.boolean()),
		isTransportExpanded: z.boolean(),
		passengerPhone: z.string(),
	})
	.superRefine((data, ctx) => {
		if (
			(data.motive === ExtraTripMotive.Absenteeism ||
				data.motive === ExtraTripMotive.SpecialServices) &&
			!data.detail.trim()
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'O detalhamento é obrigatório',
				path: ['detail'],
			});
		}

		if (data.motive === ExtraTripMotive.Absenteeism) {
			if (!data.category) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'A categoria é obrigatória para Absenteísmo',
					path: ['category'],
				});
			}
			if (!data.coveredEmployee) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'O colaborador coberto é obrigatório para Absenteísmo',
					path: ['coveredEmployee'],
				});
			}
		}

		if (data.motive === ExtraTripMotive.VacancyCovering) {
			if (!data.team) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'A turma é obrigatória para Cobertura de Vaga',
					path: ['team'],
				});
			}
			if (!data.vacancy.trim()) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: 'A vaga é obrigatória para Cobertura de Vaga',
					path: ['vacancy'],
				});
			}
		}

		if (
			data.dateIntervals.length === 0 ||
			data.dateIntervals.some(
				(interval) =>
					!interval.dateRange?.startDate || !interval.dateRange?.endDate,
			)
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Todos os períodos devem ter data de início e fim',
				path: ['dateIntervals'],
			});
		}

		if (data.dateIntervals.some((interval) => !interval.location)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Todos os períodos devem ter um local selecionado',
				path: ['dateIntervals'],
			});
		}

		if (
			data.dateIntervals.some(
				(interval) => !interval.entryTime || !interval.exitTime,
			)
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Todos os períodos devem ter horário de entrada e saída',
				path: ['dateIntervals'],
			});
		}

		const isTransportNeeded = data.dateIntervals.some(
			(interval) => interval.needTransport,
		);

		if (isTransportNeeded && !data.passengerPhone.trim()) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message:
					'O número de telefone é obrigatório quando há necessidade de transporte',
				path: ['passengerPhone'],
			});
		}

		if (isTransportNeeded) {
			const intervalsWithTransport = data.dateIntervals.filter(
				(interval) => interval.needTransport,
			);
			if (intervalsWithTransport.some((interval) => !interval.location)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						'O local é obrigatório para períodos com necessidade de transporte',
					path: ['dateIntervals'],
				});
			}

			const transportDaysWithMissingData = data.transportDays.filter(
				(day) =>
					!day.entryTime ||
					!day.exitTime ||
					!day.transportType ||
					!day.location,
			);
			if (transportDaysWithMissingData.length > 0) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message:
						'Todos os dias com transporte devem ter horários, tipo de transporte e local preenchidos',
					path: ['transportDays'],
				});
			}
		}
	});

export type ExtraTripFormData = z.infer<typeof ExtraTripFormSchema>;
